/**
 * Service pour la gestion admin des payins (commissions dues par propriétaires)
 * Interface avec l'API backend pour les fonctionnalités d'administration
 */

import api from './api';

class AdminPayinService {
  constructor() {
    this.baseUrl = '/admin/financial/payins';
  }

  // ================================
  // DASHBOARD & VUE D'ENSEMBLE
  // ================================

  /**
   * Récupère la vue d'ensemble des payins (métriques globales)
   * @param {Object} filters - Filtres optionnels
   * @returns {Promise<Object>} Vue d'ensemble avec métriques
   */
  async getOverview(filters = {}) {
    try {
      console.log('🔍 AdminPayinService.getOverview: Starting', {
        filters,
        timestamp: new Date().toISOString()
      });

      const params = new URLSearchParams();
      
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.ownerId) params.append('ownerId', filters.ownerId);

      // Add cache-busting parameter
      params.append('_t', Date.now().toString());

      const queryString = params.toString();
      const fullUrl = `${this.baseUrl}/overview?${queryString}`;

      console.log('🌐 AdminPayinService.getOverview: Making request', {
        baseUrl: this.baseUrl,
        queryString,
        fullUrl
      });

      const response = await api.get(fullUrl);
      
      console.log('📡 AdminPayinService.getOverview: Raw response', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        headers: response.headers
      });
      
      if (response.data.success) {
        console.log('✅ AdminPayinService.getOverview: Success', {
          data: response.data.data
        });
        return response.data.data;
      } else {
        console.error('❌ AdminPayinService.getOverview: API returned success=false', {
          message: response.data.message,
          data: response.data
        });
        throw new Error(response.data.message || 'Failed to fetch overview');
      }
    } catch (error) {
      console.error('❌ AdminPayinService.getOverview error:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }

  // ================================
  // GESTION DES PAYINS
  // ================================

  /**
   * Récupère la liste paginée des payins avec filtres avancés
   * @param {Object} options - Options de filtrage et pagination
   * @returns {Promise<Object>} Liste paginée des payins
   */
  async getPayins(options = {}) {
    try {
      console.log('🔍 AdminPayinService.getPayins: Starting', {
        options,
        timestamp: new Date().toISOString()
      });

      const params = new URLSearchParams();
      
      // Filtres
      if (options.status) params.append('status', options.status);
      if (options.ownerId) params.append('ownerId', options.ownerId);
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);
      if (options.minAmount) params.append('minAmount', options.minAmount.toString());
      if (options.maxAmount) params.append('maxAmount', options.maxAmount.toString());
      if (options.overdue_only) params.append('overdue_only', options.overdue_only.toString());
      if (options.search) params.append('search', options.search);
      
      // Pagination et tri
      params.append('sortBy', options.sortBy || 'created_at');
      params.append('sortOrder', options.sortOrder || 'DESC');
      params.append('limit', (options.limit || 20).toString());
      params.append('offset', (options.offset || 0).toString());
      
      // Includes
      params.append('include_owner', (options.include_owner !== false).toString());
      params.append('include_reservation', (options.include_reservation !== false).toString());

      // Add cache-busting parameter
      params.append('_t', Date.now().toString());

      const queryString = params.toString();
      const fullUrl = `${this.baseUrl}?${queryString}`;

      console.log('🌐 AdminPayinService.getPayins: Making request', {
        baseUrl: this.baseUrl,
        queryString,
        fullUrl
      });

      const response = await api.get(fullUrl);
      
      console.log('📡 AdminPayinService.getPayins: Raw response', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        dataType: typeof response.data,
        dataKeys: response.data ? Object.keys(response.data) : null
      });
      
      if (response.data.success) {
        console.log('✅ AdminPayinService.getPayins: Success', {
          data: response.data.data,
          payinsCount: response.data.data?.payins?.length || 0,
          totalItems: response.data.data?.pagination?.total_items || 0
        });
        return response.data.data;
      } else {
        console.error('❌ AdminPayinService.getPayins: API returned success=false', {
          message: response.data.message,
          data: response.data
        });
        throw new Error(response.data.message || 'Failed to fetch payins');
      }
    } catch (error) {
      console.error('❌ AdminPayinService.getPayins error:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }

  /**
   * Récupère les détails d'un payin spécifique
   * @param {string} payinId - ID du payin
   * @returns {Promise<Object>} Détails du payin
   */
  async getPayinDetails(payinId) {
    try {
      const response = await api.get(`${this.baseUrl}/${payinId}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch payin details');
      }
    } catch (error) {
      console.error('❌ AdminPayinService.getPayinDetails error:', error);
      throw error;
    }
  }

  // ================================
  // ACTIONS ADMINISTRATIVES
  // ================================

  /**
   * Marque un payin comme payé (action admin)
   * @param {string} payinId - ID du payin
   * @param {Object} paymentData - Données du paiement
   * @returns {Promise<Object>} Résultat de l'action
   */
  async markAsPaid(payinId, paymentData) {
    try {
      const response = await api.put(`${this.baseUrl}/${payinId}/mark-paid`, {
        payment_method: paymentData.paymentMethod || 'manual',
        payment_reference: paymentData.paymentReference,
        admin_notes: paymentData.adminNotes || null,
        override_amount: paymentData.overrideAmount || null
      });
      
      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to mark payin as paid');
      }
    } catch (error) {
      console.error('❌ AdminPayinService.markAsPaid error:', error);
      throw error;
    }
  }

  /**
   * Ajoute des notes administratives à un payin
   * @param {string} payinId - ID du payin
   * @param {string} notes - Notes à ajouter
   * @returns {Promise<Object>} Résultat de l'action
   */
  async addNotes(payinId, notes) {
    try {
      const response = await api.put(`${this.baseUrl}/${payinId}/notes`, {
        notes: notes
      });
      
      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to add notes');
      }
    } catch (error) {
      console.error('❌ AdminPayinService.addNotes error:', error);
      throw error;
    }
  }

  /**
   * Crée un payin manuel (action admin)
   * @param {Object} payinData - Données du payin à créer
   * @returns {Promise<Object>} Payin créé
   */
  async createManual(payinData) {
    try {
      const response = await api.post(`${this.baseUrl}/manual`, {
        owner_id: payinData.ownerId,
        reservation_id: payinData.reservationId || null,
        commission_amount: payinData.commissionAmount,
        taxes: payinData.taxes || 0,
        currency: payinData.currency || 'EUR',
        payin_type: payinData.payinType || 'commission',
        due_date: payinData.dueDate,
        notes: payinData.notes || null,
        cash_amount_received: payinData.cashAmountReceived || null
      });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create manual payin');
      }
    } catch (error) {
      console.error('❌ AdminPayinService.createManual error:', error);
      throw error;
    }
  }

  /**
   * Traite automatiquement les payins en retard
   * @returns {Promise<Object>} Résultat du traitement
   */
  async processOverdue() {
    try {
      const response = await api.post(`${this.baseUrl}/process-overdue`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to process overdue payins');
      }
    } catch (error) {
      console.error('❌ AdminPayinService.processOverdue error:', error);
      throw error;
    }
  }

  // ================================
  // UTILITAIRES & HELPERS
  // ================================

  /**
   * Formate un montant avec la devise
   * @param {number} amount - Montant
   * @param {string} currency - Devise
   * @returns {string} Montant formaté
   */
  formatAmount(amount, currency = 'EUR') {
    const formatter = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    });
    return formatter.format(amount);
  }

  /**
   * Formate une date
   * @param {string|Date} date - Date à formater
   * @returns {string} Date formatée
   */
  formatDate(date) {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Calcule le nombre de jours entre deux dates
   * @param {string|Date} startDate - Date de début
   * @param {string|Date} endDate - Date de fin (optionnelle, par défaut aujourd'hui)
   * @returns {number} Nombre de jours
   */
  calculateDaysOverdue(startDate, endDate = new Date()) {
    if (!startDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end - start;
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  }

  /**
   * Obtient la couleur selon le statut du payin
   * @param {string} status - Statut du payin
   * @returns {string} Couleur Material-UI
   */
  getStatusColor(status) {
    const colors = {
      pending: 'warning',
      processing: 'info',
      completed: 'success',
      overdue: 'error',
      cancelled: 'default'
    };
    return colors[status] || 'default';
  }

  /**
   * Obtient l'icône selon le statut du payin
   * @param {string} status - Statut du payin
   * @returns {string} Nom de l'icône Material-UI
   */
  getStatusIcon(status) {
    const icons = {
      pending: 'PendingActions',
      processing: 'Loop',
      completed: 'CheckCircle',
      overdue: 'Warning',
      cancelled: 'Cancel'
    };
    return icons[status] || 'Help';
  }

  /**
   * Obtient le label français du statut
   * @param {string} status - Statut du payin
   * @returns {string} Label français
   */
  getStatusLabel(status) {
    const labels = {
      pending: 'En attente',
      processing: 'En traitement',
      completed: 'Payé',
      overdue: 'En retard',
      cancelled: 'Annulé'
    };
    return labels[status] || status;
  }

  /**
   * Obtient le niveau de priorité selon les jours de retard
   * @param {number} daysOverdue - Nombre de jours de retard
   * @returns {Object} Niveau de priorité avec couleur et label
   */
  getPriorityLevel(daysOverdue) {
    if (daysOverdue <= 0) {
      return { level: 'normal', color: 'success', label: 'Normal' };
    } else if (daysOverdue <= 7) {
      return { level: 'warning', color: 'warning', label: 'Attention' };
    } else if (daysOverdue <= 30) {
      return { level: 'urgent', color: 'error', label: 'Urgent' };
    } else {
      return { level: 'critical', color: 'error', label: 'Critique' };
    }
  }

  /**
   * Génère des statistiques rapides à partir d'une liste de payins
   * @param {Array} payins - Liste des payins
   * @returns {Object} Statistiques calculées
   */
  calculateQuickStats(payins) {
    if (!Array.isArray(payins) || payins.length === 0) {
      return {
        total: 0,
        totalAmount: 0,
        pending: 0,
        overdue: 0,
        completed: 0,
        averageAmount: 0,
        overdueAmount: 0
      };
    }

    const stats = payins.reduce((acc, payin) => {
      acc.total += 1;
      acc.totalAmount += parseFloat(payin.commission_amount || 0);
      
      if (payin.status === 'pending') acc.pending += 1;
      if (payin.status === 'overdue') {
        acc.overdue += 1;
        acc.overdueAmount += parseFloat(payin.commission_amount || 0);
      }
      if (payin.status === 'completed') acc.completed += 1;
      
      return acc;
    }, {
      total: 0,
      totalAmount: 0,
      pending: 0,
      overdue: 0,
      completed: 0,
      overdueAmount: 0
    });

    stats.averageAmount = stats.total > 0 ? stats.totalAmount / stats.total : 0;
    
    return stats;
  }
}

export default new AdminPayinService();